import { FC, useEffect, useState } from 'react';
import { useController, useForm } from 'react-hook-form';
import { RoleGuard, CampaignAttributes } from 'portal-commons';

import { CampaignDetail } from '@/apis/core';
import { EmbeddedLinkPopup } from '..';
import {
  isValidInput,
  createMaxLengthValidator,
  isOptionRequired,
} from '../../validators';
import Section from './Section';
import SectionGrid from './SectionGrid';

interface ProgramAttributesSectionProps {
  campaign: CampaignDetail;
  readonly: boolean;
  handleAttributeChange: (
    value: boolean,
    key: string,
    label: string
  ) => Promise<void>;
  onDataChange?: (value: Record<string, unknown>) => void;
}

enum AdditionalSetupType {
  None,
  EmbeddedLink,
}

const ProgramAttributesSection: FC<ProgramAttributesSectionProps> = ({
  campaign,
  readonly,
  handleAttributeChange,
  onDataChange,
}) => {
  const [additionalSetupType, setAdditionalSetupType] = useState(
    AdditionalSetupType.None
  );
  const {
    control,
    reset,
    clearErrors,
    formState: { dirtyFields },
  } = useForm<{
    embeddedLinkSample: string;
    embeddedLink: boolean;
    embeddedPhone: boolean;
  }>({
    mode: 'onTouched',
    defaultValues: {
      embeddedLinkSample: campaign.embeddedLinkSample || '',
      embeddedLink: campaign.embeddedLink,
      embeddedPhone: campaign.embeddedPhone,
    },
  });

  const {
    field: {
      value: embeddedLinkSample,
      onChange: setEmbeddedLinkSample,
      onBlur: onEmbeddedLinkSampleBlur,
    },
    fieldState: { error: embeddedLinkSampleError },
  } = useController({
    name: 'embeddedLinkSample',
    control,
    rules: {
      validate: {
        isValidInput,
        withValidMaxLength: createMaxLengthValidator(255),
      },
    },
  });

  const {
    field: { value: embeddedLink, onChange: setEmbeddedLink },
    fieldState: { error: embeddedLinkError },
  } = useController({
    control,
    name: 'embeddedLink',
    rules: {
      validate: {
        isOptionRequired,
      },
    },
  });

  const {
    field: { value: embeddedPhone, onChange: setEmbeddedPhone },
    fieldState: { error: embeddedPhoneError },
  } = useController({
    control,
    name: 'embeddedPhone',
    rules: {
      validate: {
        isOptionRequired,
      },
    },
  });

  const handleAdditionSetupVisibilityChange = (
    type: AdditionalSetupType,
    open: boolean
  ) => {
    if (open) {
      setAdditionalSetupType(type);
    } else if (additionalSetupType === type) {
      // Prevent additional setup from closing when switching additional setup type
      setAdditionalSetupType(AdditionalSetupType.None);
    }
  };

  useEffect(() => {
    reset({
      embeddedLinkSample: campaign.embeddedLinkSample || '',
      embeddedLink: campaign.embeddedLink,
      embeddedPhone: campaign.embeddedPhone,
    });
    clearErrors();
  }, [campaign]);

  return (
    <Section title="Program Attributes">
      <SectionGrid
        sx={{
          gridTemplateColumns: '1fr 1fr',
        }}
      >
        <RoleGuard
          feature="campaignDetail.editCampaignAttributes"
          render={(authorized) => (
            <CampaignAttributes.Attribute
              id="embeddedLink"
              label="Embedded Link"
              tooltip="Indicates whether the program is using an embedded link of any kind. Note that public URL shorteners (bitly, tinyurl) are not accepted"
              open={additionalSetupType === AdditionalSetupType.EmbeddedLink}
              value={embeddedLink}
              onChange={(val) => {
                setEmbeddedLink(val);
                handleAttributeChange(val, 'embeddedLink', 'Embedded Link');
              }}
              error={
                embeddedLinkError?.message || embeddedLinkSampleError?.message
              }
              disabledOption={readonly || !authorized ? 'all' : undefined}
              additionalSetup={
                <EmbeddedLinkPopup
                  disabled={readonly}
                  editable={authorized}
                  defaultReadonly
                  hideCloseButton
                  value={embeddedLinkSample}
                  error={embeddedLinkSampleError?.message}
                  onChange={setEmbeddedLinkSample}
                  onBlur={onEmbeddedLinkSampleBlur}
                  onClose={() => {
                    if (
                      onDataChange &&
                      dirtyFields.embeddedLinkSample &&
                      !embeddedLinkSampleError?.message
                    ) {
                      onDataChange({ embeddedLinkSample });
                    }
                    setAdditionalSetupType(AdditionalSetupType.None);
                  }}
                />
              }
              onVisibilityChange={(open) =>
                handleAdditionSetupVisibilityChange(
                  AdditionalSetupType.EmbeddedLink,
                  open
                )
              }
            />
          )}
        />

        <RoleGuard
          feature="campaignDetail.editCampaignAttributes"
          render={(authorized) => (
            <CampaignAttributes.Attribute
              id="embeddedPhone"
              label="Embedded Phone Number"
              tooltip="Indicates whether the program is using an embedded phone number (except the required HELP information contact phone number)"
              disabledOption={readonly || !authorized ? 'all' : undefined}
              value={embeddedPhone}
              error={embeddedPhoneError?.message}
              onChange={(val) => {
                setEmbeddedPhone(val);
                handleAttributeChange(val, 'embeddedPhone', 'Embedded Phone');
              }}
            />
          )}
        />
      </SectionGrid>
    </Section>
  );
};

export default ProgramAttributesSection;
